import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck, Lock, CheckCircle2, AlertCircle, CreditCard } from "lucide-react";
import { Overline } from "@/components/motion";
import api, { getStage, getRegistrationAvailability, createRegistration, createCheckoutSession } from "@/lib/api";
import { formatDateRange, formatDateFR, formatPrice, categoryLabel, stageBasePath } from "@/lib/format";

const POSTES = ["Gardien", "Défenseur central", "Latéral", "Milieu défensif", "Milieu central", "Milieu offensif", "Ailier", "Attaquant"];
const SOCK_SIZES = ["31/34", "35/38", "39/42", "43/46"];
const KIT_SIZES = ["116", "128", "140", "152", "164", "S (adulte)"]; // short & maillot — marque Puma

const EMPTY_FORM = {
  child_last_name: "",
  child_first_name: "",
  child_birth_date: "",
  category: "",
  email: "",
  mother_phone: "",
  father_phone: "",
  current_club: "",
  playing_position: "",
  player_type: "joueur",
  jersey_size: "",
  short_size: "",
  sock_size: "",
  promo_code: "",
  doctor_name: "",
  food_allergies: "",
  medical_history: "",
  image_rights_accepted: false,
};

const inputClass =
  "w-full bg-[#050505] border border-white/10 text-white px-4 py-3.5 text-base focus:border-gold focus:ring-1 focus:ring-gold outline-none transition-all placeholder:text-white/25";

const Field = ({ label, error, children, testId }) => (
  <div data-testid={testId}>
    <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-sm text-rouge-hover">{error}</p>}
  </div>
);

const Section = ({ num, title, children, note }) => (
  <section className="bg-noir-surface border border-noir-border p-6 md:p-10">
    <div className="flex items-baseline gap-4 mb-7">
      <span className="font-display text-4xl text-stroke-gold leading-none">{num}</span>
      <h2 className="font-display uppercase text-2xl md:text-3xl tracking-wide">{title}</h2>
    </div>
    {note && (
      <p className="mb-6 flex items-start gap-3 text-sm text-white/55 bg-white/[0.03] border border-white/10 p-4">
        <Lock size={15} className="text-gold shrink-0 mt-0.5" />
        {note}
      </p>
    )}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">{children}</div>
  </section>
);

const validate = (form, stage) => {
  const errors = {};
  if (!form.child_last_name.trim()) errors.child_last_name = "Veuillez renseigner le nom de l'enfant.";
  if (!form.child_first_name.trim()) errors.child_first_name = "Veuillez renseigner le prénom de l'enfant.";
  if (!form.child_birth_date) {
    errors.child_birth_date = "Veuillez renseigner la date de naissance de l'enfant.";
  } else if (new Date(form.child_birth_date) >= new Date()) {
    errors.child_birth_date = "La date de naissance doit être dans le passé.";
  }
  if (!form.category) {
    errors.category = "Veuillez sélectionner la catégorie de l'enfant.";
  } else if (stage && !stage.categories.includes(form.category)) {
    errors.category = "Cette catégorie n'est pas proposée pour ce stage.";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Veuillez renseigner une adresse email valide.";
  const phoneRe = /^[+0-9 ().-]{8,20}$/;
  if (form.mother_phone && !phoneRe.test(form.mother_phone)) errors.mother_phone = "Veuillez renseigner un numéro de téléphone valide.";
  if (form.father_phone && !phoneRe.test(form.father_phone)) errors.father_phone = "Veuillez renseigner un numéro de téléphone valide.";
  if (!form.mother_phone && !form.father_phone) errors.mother_phone = "Renseignez au moins un numéro de téléphone de contact.";
  if (!form.jersey_size) errors.jersey_size = "Sélectionnez la taille du maillot.";
  if (!form.short_size) errors.short_size = "Sélectionnez la taille du short.";
  if (!form.sock_size) errors.sock_size = "Sélectionnez la taille des chaussettes.";
  return errors;
};

const RecapRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 py-3 border-b border-white/5">
    <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{label}</span>
    <span className="text-white text-sm font-semibold text-left sm:text-right">{value || "—"}</span>
  </div>
);

const StepsBar = ({ step }) => {
  const steps = ["Formulaire", "Récapitulatif", "Paiement"];
  const current = step === "form" ? 0 : step === "recap" ? 1 : 2;
  return (
    <div data-testid="inscription-steps" className="flex items-center gap-3 md:gap-5 mb-12">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-3 md:gap-5">
          <div className="flex items-center gap-2.5">
            <span
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border transition-colors ${
                i <= current ? "bg-gold text-noir border-gold" : "border-white/20 text-white/40"
              }`}
            >
              {i + 1}
            </span>
            <span className={`text-[11px] md:text-xs font-bold uppercase tracking-[0.15em] ${i <= current ? "text-gold" : "text-white/35"}`}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && <span className="w-6 md:w-14 h-px bg-white/15" />}
        </div>
      ))}
    </div>
  );
};

// Champs NON sensibles restaurables en cas de rafraîchissement (jamais les données médicales)
const PERSISTED_KEYS = ["child_last_name", "child_first_name", "child_birth_date", "category", "email", "mother_phone", "father_phone", "current_club", "playing_position", "player_type", "jersey_size", "short_size", "sock_size", "promo_code"];

const Inscription = () => {
  const { slug } = useParams();
  const [stage, setStage] = useState(null);
  const [pageState, setPageState] = useState("loading"); // loading | ready | full | closed | notfound
  const [step, setStep] = useState("form"); // form | recap | ready
  const [form, setForm] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`inscription-${slug}`);
      return saved ? { ...EMPTY_FORM, ...JSON.parse(saved) } : EMPTY_FORM;
    } catch {
      return EMPTY_FORM;
    }
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_percent}
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [registration, setRegistration] = useState(null);
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    (async () => {
      const s = await getStage(slug);
      if (!s) {
        setPageState("notfound");
        return;
      }
      try {
        const availability = await getRegistrationAvailability(slug);
        if (availability.status === "full") setPageState("full");
        else if (availability.status === "closed") setPageState("closed");
        else {
          setStage(s);
          setPageState("ready");
        }
      } catch {
        setStage(s);
        setPageState("ready");
      }
    })();
  }, [slug]);

  const update = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [key]: value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const finalPrice = stage && promoApplied
    ? Math.max(0, Math.round(stage.price * (1 - promoApplied.discount_percent / 100) * 100) / 100)
    : stage?.price;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoChecking(true);
    setPromoError("");
    try {
      const { data } = await api.post("/promo-codes/validate", { code: promoInput.trim() });
      setPromoApplied({ code: data.code, discount_percent: data.discount_percent });
      setForm((f) => ({ ...f, promo_code: data.code }));
      toast.success(`Code ${data.code} appliqué : −${data.discount_percent} %`);
    } catch (e) {
      setPromoApplied(null);
      setForm((f) => ({ ...f, promo_code: "" }));
      setPromoError(e.response?.data?.detail || "Code promo invalide.");
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
    setPromoError("");
    setForm((f) => ({ ...f, promo_code: "" }));
  };

  const goToRecap = (e) => {
    e.preventDefault();
    const errs = validate(form, stage);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Veuillez corriger les champs indiqués.");
      return;
    }
    setStep("recap");
    window.scrollTo({ top: 0 });
  };

  const confirmRegistration = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await createRegistration({
        ...form,
        stage_id: stage.id,
        idempotency_key: idempotencyKey.current,
      });
      setRegistration(res);
      setStep("ready");
      sessionStorage.removeItem(`inscription-${slug}`);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err.message);
      if (err.message.includes("COMPLET")) setPageState("full");
      if (err.message.includes("FERMÉES")) setPageState("closed");
    } finally {
      setSubmitting(false);
    }
  };

  const goToPayment = async () => {
    if (paying || !registration) return;
    setPaying(true);
    try {
      const { checkout_url } = await createCheckoutSession(registration.id);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.message);
      setPaying(false);
    }
  };

  if (pageState === "loading") {
    return (
      <div data-testid="inscription-loading" className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-gold" />
      </div>
    );
  }

  if (pageState === "notfound" || pageState === "full" || pageState === "closed") {
    const messages = {
      notfound: { title: "STAGE INTROUVABLE", text: "Ce stage n'existe pas ou n'est plus disponible." },
      full: { title: "CE STAGE EST COMPLET", text: "Toutes les places ont trouvé preneur. Découvrez nos autres sessions." },
      closed: { title: "LES INSCRIPTIONS SONT FERMÉES", text: "Les inscriptions ne sont plus ouvertes pour ce stage." },
    };
    const m = messages[pageState];
    return (
      <div data-testid={`inscription-blocked-${pageState}`} className="min-h-screen flex flex-col items-center justify-center px-6 text-center pt-20">
        <AlertCircle size={44} className="text-gold" />
        <h1 className="mt-6 font-display uppercase text-5xl md:text-6xl text-white">{m.title}</h1>
        <p className="mt-4 text-white/60 max-w-md">{m.text}</p>
        <Link to="/stage-payet" data-testid="inscription-blocked-back" className="mt-10 px-8 py-4 border-2 border-gold text-gold text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all">
          Voir les stages
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="inscription-page" className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-24 md:pb-32 min-h-screen">
      <div className="max-w-3xl mx-auto">
        <Link
          to={`${stageBasePath(stage)}/${stage.slug}`}
          data-testid="inscription-back-to-stage"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-gold transition-colors"
        >
          <ArrowLeft size={15} /> Retour au stage
        </Link>

        <Overline className="mt-8">Inscription</Overline>
        <h1 className="mt-3 font-display uppercase text-5xl md:text-6xl leading-[0.9] tracking-tight">
          {step === "recap" ? "Récapitulatif" : step === "ready" ? "Inscription prête" : "Formulaire"}
        </h1>

        <div data-testid="inscription-stage-summary" className="mt-8 bg-noir-surface border border-gold/25 p-6 md:p-8 flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <p className="font-display text-2xl tracking-wide text-gold uppercase">{stage.name}</p>
            <p className="text-white/60 text-sm mt-1">
              {formatDateRange(stage.start_date, stage.end_date)} — {stage.duration_days} jours — {stage.location}
            </p>
          </div>
          <div className="flex items-center gap-6 ml-auto">
            <span className="text-xs font-bold tracking-[0.15em] text-white/60 uppercase">{categoryLabel(stage.categories)}</span>
            <span className="font-display text-3xl text-gold">{formatPrice(stage.price)}</span>
          </div>
        </div>

        <div className="mt-10">
          <StepsBar step={step} />
        </div>

        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.form
              key="form"
              data-testid="inscription-form"
              onSubmit={goToRecap}
              noValidate
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="space-y-8"
            >
              <Section num="01" title="Informations de l'enfant">
                <Field label="Nom de l'enfant *" error={errors.child_last_name} testId="field-child-last-name">
                  <input data-testid="input-child-last-name" type="text" value={form.child_last_name} onChange={update("child_last_name")} placeholder="Nom" className={inputClass} />
                </Field>
                <Field label="Prénom de l'enfant *" error={errors.child_first_name} testId="field-child-first-name">
                  <input data-testid="input-child-first-name" type="text" value={form.child_first_name} onChange={update("child_first_name")} placeholder="Prénom" className={inputClass} />
                </Field>
                <Field label="Date de naissance *" error={errors.child_birth_date} testId="field-child-birth-date">
                  <input data-testid="input-child-birth-date" type="date" value={form.child_birth_date} max={new Date().toISOString().slice(0, 10)} onChange={update("child_birth_date")} className={`${inputClass} [color-scheme:dark]`} />
                </Field>
                <Field label="Catégorie de l'enfant *" error={errors.category} testId="field-category">
                  <select data-testid="select-category" value={form.category} onChange={update("category")} className={inputClass}>
                    <option value="">Sélectionner…</option>
                    {stage.categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </Field>
              </Section>

              <Section num="02" title="Informations sportives">
                <div className="sm:col-span-2" data-testid="field-player-type">
                  <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">Gardien ou joueur</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "joueur", label: "Joueur" },
                      { value: "gardien", label: "Gardien" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`player-type-${opt.value}`}
                        onClick={() => setForm({ ...form, player_type: opt.value })}
                        className={`py-3.5 text-sm font-bold uppercase tracking-[0.15em] border transition-all ${
                          form.player_type === opt.value
                            ? "bg-gold text-noir border-gold"
                            : "border-white/15 text-white/60 hover:border-gold/50 hover:text-white"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Field label="Poste joué" testId="field-playing-position">
                  <select data-testid="select-playing-position" value={form.playing_position} onChange={update("playing_position")} className={inputClass}>
                    <option value="">Sélectionner…</option>
                    {POSTES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Club actuel" testId="field-current-club">
                  <input data-testid="input-current-club" type="text" value={form.current_club} onChange={update("current_club")} placeholder="Ex : OGC Nice" className={inputClass} />
                </Field>

                <div className="sm:col-span-2 border-t border-white/10 pt-6 mt-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold mb-1">Tenue officielle du stagiaire *</p>
                  <p className="text-xs text-white/40 mb-5">Chaque stagiaire repart avec son pack joueurs : indiquez les tailles (short et maillot : tailles Puma).</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Maillot *" error={errors.jersey_size} testId="field-jersey-size">
                      <select data-testid="select-jersey-size" value={form.jersey_size} onChange={update("jersey_size")} className={inputClass}>
                        <option value="">Taille…</option>
                        {KIT_SIZES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Short *" error={errors.short_size} testId="field-short-size">
                      <select data-testid="select-short-size" value={form.short_size} onChange={update("short_size")} className={inputClass}>
                        <option value="">Taille…</option>
                        {KIT_SIZES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Chaussettes *" error={errors.sock_size} testId="field-sock-size">
                      <select data-testid="select-sock-size" value={form.sock_size} onChange={update("sock_size")} className={inputClass}>
                        <option value="">Taille…</option>
                        {SOCK_SIZES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>
              </Section>

              <Section num="03" title="Contact responsable">
                <Field label="Adresse email *" error={errors.email} testId="field-email">
                  <input data-testid="input-email" type="email" inputMode="email" value={form.email} onChange={update("email")} placeholder="vous@exemple.fr" className={inputClass} />
                </Field>
                <div className="hidden sm:block" />
                <Field label="Téléphone mère" error={errors.mother_phone} testId="field-mother-phone">
                  <input data-testid="input-mother-phone" type="tel" inputMode="tel" value={form.mother_phone} onChange={update("mother_phone")} placeholder="06 12 34 56 78" className={inputClass} />
                </Field>
                <Field label="Téléphone père" error={errors.father_phone} testId="field-father-phone">
                  <input data-testid="input-father-phone" type="tel" inputMode="tel" value={form.father_phone} onChange={update("father_phone")} placeholder="06 12 34 56 78" className={inputClass} />
                </Field>
                <p className="sm:col-span-2 text-white/40 text-xs">Au moins un numéro de téléphone est nécessaire pour vous contacter pendant le stage.</p>
              </Section>

              <Section
                num="04"
                title="Informations médicales"
                note="Ces informations sont strictement confidentielles. Elles sont utilisées uniquement dans le cadre de l'accueil et de la sécurité de votre enfant pendant le stage, et ne sont accessibles qu'à l'équipe encadrante autorisée."
              >
                <Field label="Nom du médecin traitant" testId="field-doctor-name">
                  <input data-testid="input-doctor-name" type="text" value={form.doctor_name} onChange={update("doctor_name")} placeholder="Dr …" className={inputClass} />
                </Field>
                <div className="hidden sm:block" />
                <div className="sm:col-span-2">
                  <Field label="Allergies alimentaires" testId="field-food-allergies">
                    <textarea data-testid="input-food-allergies" rows={2} value={form.food_allergies} onChange={update("food_allergies")} placeholder="Ex : arachides, gluten…" className={`${inputClass} resize-y`} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Antécédents médicaux" testId="field-medical-history">
                    <textarea data-testid="input-medical-history" rows={2} value={form.medical_history} onChange={update("medical_history")} placeholder="Informations utiles à l'encadrement…" className={`${inputClass} resize-y`} />
                  </Field>
                </div>
              </Section>

              <Section num="05" title="Droit à l'image">
                <div className="sm:col-span-2">
                  <label
                    data-testid="image-rights-field"
                    className="flex items-start gap-4 cursor-pointer border border-white/10 hover:border-gold/40 transition-colors p-5"
                  >
                    <input
                      data-testid="checkbox-image-rights"
                      type="checkbox"
                      checked={form.image_rights_accepted}
                      onChange={update("image_rights_accepted")}
                      className="mt-1 w-5 h-5 shrink-0 accent-[#D4AF37]"
                    />
                    <span className="text-sm text-white/70 leading-relaxed">
                      J'autorise 3M Match Pro à photographier et/ou filmer mon enfant pendant les stages et à utiliser
                      ces images dans le cadre de sa communication (site internet, réseaux sociaux et supports promotionnels).
                    </span>
                  </label>
                  <p className="mt-2 text-white/35 text-xs">Cette case n'est jamais cochée par défaut. Votre choix est enregistré avec sa date.</p>
                </div>
              </Section>

              <div data-testid="rgpd-notice" className="border border-white/10 bg-white/[0.02] p-6 text-sm text-white/50 leading-relaxed">
                <p className="flex items-start gap-3">
                  <ShieldCheck size={16} className="text-gold shrink-0 mt-0.5" />
                  <span>
                    Les informations recueillies font l'objet d'un traitement destiné à la gestion de votre inscription.
                    Elles ne sont jamais diffusées publiquement. Vous disposez d'un droit d'accès, de rectification et de
                    suppression de vos données.{" "}
                    <Link to="/politique-confidentialite" data-testid="rgpd-privacy-link" className="text-gold hover:text-gold-light underline underline-offset-4">
                      Consulter notre politique de confidentialité
                    </Link>
                  </span>
                </p>
              </div>

              <button
                type="submit"
                data-testid="inscription-submit-button"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_24px_rgba(230,0,0,0.35)]"
              >
                Vérifier mon inscription <ArrowRight size={16} />
              </button>
            </motion.form>
          )}

          {step === "recap" && (
            <motion.div
              key="recap"
              data-testid="inscription-recap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="space-y-8"
            >
              <div className="bg-noir-surface border border-noir-border p-6 md:p-10">
                <h2 className="font-display uppercase text-3xl tracking-wide text-gold">Récapitulatif de l'inscription</h2>

                <div className="mt-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Stage</p>
                  <RecapRow label="Nom du stage" value={stage.name} />
                  <RecapRow label="Dates" value={formatDateRange(stage.start_date, stage.end_date)} />
                  <RecapRow label="Lieu" value={stage.location} />
                  <RecapRow label="Catégorie" value={form.category} />
                </div>

                <div className="mt-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Enfant</p>
                  <RecapRow label="Nom" value={form.child_last_name} />
                  <RecapRow label="Prénom" value={form.child_first_name} />
                  <RecapRow label="Date de naissance" value={formatDateFR(form.child_birth_date)} />
                  <RecapRow label="Club" value={form.current_club || "Non renseigné"} />
                  <RecapRow label="Poste" value={form.playing_position || "Non renseigné"} />
                  <RecapRow label="Gardien / Joueur" value={form.player_type === "gardien" ? "Gardien" : "Joueur"} />
                  <RecapRow label="Taille maillot" value={form.jersey_size} />
                  <RecapRow label="Taille short" value={form.short_size} />
                  <RecapRow label="Taille chaussettes" value={form.sock_size} />
                </div>

                <div className="mt-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Responsable</p>
                  <RecapRow label="Email" value={form.email} />
                  <RecapRow label="Téléphone mère" value={form.mother_phone || "—"} />
                  <RecapRow label="Téléphone père" value={form.father_phone || "—"} />
                </div>

                <div className="mt-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Informations médicales</p>
                  <RecapRow label="Médecin traitant" value={form.doctor_name ? "Renseigné" : "Non renseigné"} />
                  <RecapRow label="Allergies" value={form.food_allergies ? "Renseigné" : "Non renseigné"} />
                  <RecapRow label="Antécédents" value={form.medical_history ? "Renseigné" : "Non renseigné"} />
                </div>

                <div className="mt-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Droit à l'image</p>
                  <RecapRow label="Autorisation" value={form.image_rights_accepted ? "Accepté" : "Non accepté"} />
                </div>

                <div className="mt-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Code promo</p>
                  {promoApplied ? (
                    <div data-testid="promo-applied" className="flex flex-wrap items-center gap-4 border border-gold/40 bg-gold/5 px-4 py-3">
                      <span className="text-gold font-bold text-sm tracking-wider">{promoApplied.code} — −{promoApplied.discount_percent} %</span>
                      {promoApplied.discount_percent >= 100 && <span className="text-xs font-bold uppercase tracking-wider text-gold">Inscription offerte, sans paiement</span>}
                      <button type="button" data-testid="promo-remove-button" onClick={removePromo} className="text-xs text-white/50 hover:text-rouge-hover underline underline-offset-4">
                        Retirer
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        data-testid="promo-input"
                        value={promoInput}
                        onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }}
                        placeholder="Vous avez un code ? Saisissez-le ici"
                        className={`${inputClass} uppercase sm:max-w-xs`}
                      />
                      <button
                        type="button"
                        data-testid="promo-apply-button"
                        onClick={applyPromo}
                        disabled={promoChecking || !promoInput.trim()}
                        className="px-6 py-3 border-2 border-gold text-gold text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all disabled:opacity-50"
                      >
                        {promoChecking ? <Loader2 size={14} className="animate-spin" /> : "Appliquer"}
                      </button>
                    </div>
                  )}
                  {promoError && <p data-testid="promo-error" className="mt-2 text-xs text-rouge-hover">{promoError}</p>}
                </div>

                <div className="mt-10 pt-8 border-t border-gold/25 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/50">Total</span>
                  <span data-testid="recap-total-price" className="font-display text-5xl text-gold">
                    {promoApplied && promoApplied.discount_percent > 0 && (
                      <span className="text-2xl text-white/40 line-through mr-4">{formatPrice(stage.price)}</span>
                    )}
                    {finalPrice === 0 ? "Offert" : formatPrice(finalPrice)}
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  data-testid="recap-modify-button"
                  onClick={() => { setStep("form"); window.scrollTo({ top: 0 }); }}
                  className="px-8 py-4 border-2 border-gold text-gold text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all"
                >
                  Modifier mes informations
                </button>
                <button
                  type="button"
                  data-testid="recap-confirm-button"
                  onClick={confirmRegistration}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_24px_rgba(230,0,0,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  {submitting ? "Enregistrement…" : "Confirmer mon inscription"}
                </button>
              </div>
            </motion.div>
          )}

          {step === "ready" && registration && registration.payment_status === "paid" && (
            <motion.div
              key="free"
              data-testid="inscription-free-confirmed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-noir-surface border border-gold/40 p-8 md:p-12 text-center"
            >
              <CheckCircle2 size={52} className="text-gold mx-auto" />
              <h2 className="mt-6 font-display uppercase text-4xl md:text-5xl tracking-wide">Inscription <span className="text-gold">confirmée</span></h2>
              <p className="mt-3 text-gold/80 text-sm font-bold tracking-[0.2em] uppercase">
                Référence : {registration.registration_reference}
              </p>
              <p className="mt-4 text-white/65 max-w-md mx-auto">
                La place de {form.child_first_name} est confirmée — offerte grâce au code{" "}
                <strong className="text-gold">{registration.promo_code || form.promo_code}</strong>.
                Un email de confirmation vient de vous être envoyé.
              </p>
              <Link
                to="/"
                data-testid="free-back-home"
                className="mt-10 inline-flex items-center gap-3 px-10 py-4 border-2 border-gold text-gold text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all"
              >
                Retour à l'accueil
              </Link>
            </motion.div>
          )}

          {step === "ready" && registration && registration.payment_status !== "paid" && (
            <motion.div
              key="ready"
              data-testid="inscription-ready"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-noir-surface border border-gold/25 p-8 md:p-12 text-center"
            >
              <CheckCircle2 size={52} className="text-gold mx-auto" />
              <h2 className="mt-6 font-display uppercase text-4xl md:text-5xl tracking-wide">Inscription prête</h2>
              <p className="mt-3 text-gold/80 text-sm font-bold tracking-[0.2em] uppercase">
                Référence : {registration.registration_reference}
              </p>
              <p className="mt-4 text-white/65 max-w-md mx-auto">
                Vos informations ont bien été renseignées. Il ne reste plus qu'à régler le stage pour
                confirmer définitivement la place de {form.child_first_name}.
              </p>
              <div className="mt-8 inline-block border border-white/10 px-10 py-6">
                <p className="text-[11px] uppercase tracking-[0.3em] text-white/40">Montant à régler</p>
                <p data-testid="ready-amount" className="font-display text-6xl text-gold mt-2">{formatPrice(registration.amount)}</p>
              </div>
              <div className="mt-10">
                <button
                  type="button"
                  data-testid="continue-payment-button"
                  onClick={goToPayment}
                  disabled={paying}
                  className="inline-flex items-center justify-center gap-3 px-10 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_24px_rgba(230,0,0,0.35)] disabled:opacity-60"
                >
                  {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {paying ? "Redirection vers Stripe…" : "Continuer vers le paiement"}
                </button>
                <p className="mt-4 text-white/35 text-xs flex items-center justify-center gap-2">
                  <Lock size={12} /> Paiement sécurisé Stripe — la place n'est confirmée qu'après paiement validé.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Inscription;
